import { useEffect, useMemo, useRef, useState } from "react";
import { BbotButton, Checkbox, Input, PageLoadingSpinner, Select, SettingsCard } from "top-component-library";
import {
  ChecklistScroll,
  LocationAssignmentControls,
  LocationCheckboxGrid,
  LocationCheckboxRow,
  LocationCustomerGroup,
  LocationWarningText,
} from "../styles";
import {
  allLocations,
  customersById,
  filterLocationsForChecklist,
  findDuplicateCheckedShortIds,
  findUneditableLocationShortIds,
  groupLocationsByCustomerOnHostname,
  initialCheckedLocationIds,
  locationIdsMatchingShortIds,
} from "../utils";

// Mirrors edit-hostname-configs.html's "Choose Locations Accessible From {hostname}"
// block (ng-if="props.showLocationSelector && currentUser.isBbotAdmin"). Rendered with
// key={hostname} by HostnameConfigDetail, so a hostname switch always remounts this
// component fresh rather than relying on that as an unenforced assumption here.
const LocationAssignmentSection = ({
  hostname,
  locationsByCustomer,
  customers,
  fulfillmentMethods,
  prettyNames,
  isLoading,
  isSaving,
  hideSaveControls,
  onSave,
}) => {
  const [mode, setMode] = useState("display");
  // Lazy initializers: correct with no flash of empty state when the delivery-locations
  // fetch already resolved before this component mounted (the common case, since
  // useHostnameLocations starts fetching as soon as the page mounts, well before any
  // hostname is picked).
  const [checkedLocationIds, setCheckedLocationIds] = useState(() =>
    initialCheckedLocationIds(locationsByCustomer, hostname)
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => customers[0]?.customer_id ?? null);
  const [searchText, setSearchText] = useState("");
  const [chosenFulfillmentMethods, setChosenFulfillmentMethods] = useState(fulfillmentMethods);
  const [bulkPasteText, setBulkPasteText] = useState("");

  // Covers the other case, where this component mounted before the fetch resolved (the
  // lazy initializers above then ran against empty data). Seeds real state exactly once,
  // the first time isLoading goes false - never again after that, so a later isLoading
  // flip (the post-save refresh) can't wipe the admin's in-progress filter/selection
  // state the way re-seeding on every flip did before.
  const hasSeededRef = useRef(!isLoading);
  useEffect(() => {
    if (isLoading || hasSeededRef.current) return;
    hasSeededRef.current = true;
    setCheckedLocationIds(initialCheckedLocationIds(locationsByCustomer, hostname));
    setSelectedCustomerId(customers[0]?.customer_id ?? null);
    setChosenFulfillmentMethods(fulfillmentMethods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const customerNameById = useMemo(() => customersById(customers), [customers]);

  // Flattened once per data change rather than inside each of the two checks below,
  // which both re-run on every checkbox toggle.
  const flatLocations = useMemo(() => allLocations(locationsByCustomer), [locationsByCustomer]);

  const displayGroups = useMemo(
    () => groupLocationsByCustomerOnHostname(locationsByCustomer, checkedLocationIds),
    [locationsByCustomer, checkedLocationIds]
  );

  const duplicateLocationsError = useMemo(
    () => findDuplicateCheckedShortIds(flatLocations, checkedLocationIds),
    [flatLocations, checkedLocationIds]
  );

  const uneditableLocations = useMemo(
    () => findUneditableLocationShortIds(flatLocations, checkedLocationIds, hostname),
    [flatLocations, checkedLocationIds, hostname]
  );

  const visibleLocations = useMemo(
    () =>
      filterLocationsForChecklist(locationsByCustomer[selectedCustomerId] ?? [], searchText, chosenFulfillmentMethods),
    [locationsByCustomer, selectedCustomerId, searchText, chosenFulfillmentMethods]
  );

  const setLocationChecked = (locationId, checked) => {
    setCheckedLocationIds((current) => {
      const next = new Set(current);
      if (checked) next.add(locationId);
      else next.delete(locationId);
      return next;
    });
  };

  // Mirrors selectAllLocations()/deselectAllLocations(): only ever touches the
  // currently-visible (customer + search + fulfillment filtered) locations.
  const setVisibleLocationsChecked = (checked) => {
    setCheckedLocationIds((current) => {
      const next = new Set(current);
      visibleLocations.forEach((location) => {
        if (checked) next.add(location.locationId);
        else next.delete(location.locationId);
      });
      return next;
    });
  };

  // Mirrors checkLocationsForHostname(): reaches every connected customer's
  // locations, not just the one currently selected in the filter dropdown.
  const checkPastedLocations = () => {
    const shortIdsToCheck = bulkPasteText.split(",");
    const matchingIds = locationIdsMatchingShortIds(locationsByCustomer, shortIdsToCheck);
    setCheckedLocationIds((current) => new Set([...current, ...matchingIds]));
  };

  // Mirrors saveHostnameToLocations(): sends every connected customer's locations
  // with showOnHostname recomputed against the current checked set, not a diff.
  const handleSave = async () => {
    const deliveryLocations = {};
    Object.entries(locationsByCustomer).forEach(([customerId, locations]) => {
      deliveryLocations[customerId] = locations.map((location) => ({
        ...location,
        showOnHostname: checkedLocationIds.has(location.locationId),
      }));
    });
    const succeeded = await onSave(hostname, deliveryLocations);
    if (succeeded) setMode("display");
  };

  if (isLoading) {
    return (
      <SettingsCard title={`Choose Locations Accessible From ${hostname}`}>
        <PageLoadingSpinner />
      </SettingsCard>
    );
  }

  return (
    <SettingsCard title={`Choose Locations Accessible From ${hostname}`}>
      {mode === "display" ? (
        <div>
          <LocationAssignmentControls>
            <strong>Current Location Codes</strong>
            <BbotButton type="link" onClick={() => setMode("edit")}>
              Edit
            </BbotButton>
          </LocationAssignmentControls>
          <ChecklistScroll>
            {Object.entries(displayGroups).map(([customerId, locations]) => (
              <LocationCustomerGroup key={customerId}>
                <h4>{customerNameById[customerId]?.name_for_admin}</h4>
                <LocationCheckboxGrid>
                  {locations.map((location) => (
                    <LocationCheckboxRow key={location.locationId}>
                      <Checkbox id={`location-checkbox-${location.locationId}`} checked disabled />
                      <label htmlFor={`location-checkbox-${location.locationId}`}>{location.locationShortId}</label>
                    </LocationCheckboxRow>
                  ))}
                </LocationCheckboxGrid>
              </LocationCustomerGroup>
            ))}
          </ChecklistScroll>
        </div>
      ) : (
        <div>
          <LocationAssignmentControls>
            <span>Showing Location Codes For</span>
            <Select value={selectedCustomerId} onChange={setSelectedCustomerId} style={{ minWidth: 200 }}>
              {customers.map((customer) => (
                <Select.Option key={customer.customer_id} value={customer.customer_id}>
                  {customer.name_for_admin}
                </Select.Option>
              ))}
            </Select>
          </LocationAssignmentControls>
          <LocationAssignmentControls>
            <Input
              placeholder="Input locations to check separated by commas (table1,table2,...)"
              value={bulkPasteText}
              onChange={(e) => setBulkPasteText(e.target.value)}
              style={{ width: 400 }}
            />
            <BbotButton type="link" onClick={checkPastedLocations}>
              Check Locations
            </BbotButton>
          </LocationAssignmentControls>
          <LocationAssignmentControls>
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
            <BbotButton type="link" onClick={() => setVisibleLocationsChecked(true)}>
              Select All Visible Locations
            </BbotButton>
          </LocationAssignmentControls>
          <LocationAssignmentControls>
            <Select
              mode="multiple"
              value={chosenFulfillmentMethods}
              onChange={setChosenFulfillmentMethods}
              style={{ width: "70%" }}
            >
              {fulfillmentMethods.map((method) => (
                <Select.Option key={method} value={method}>
                  {prettyNames[method] ?? method}
                </Select.Option>
              ))}
            </Select>
          </LocationAssignmentControls>
          {duplicateLocationsError && (
            <LocationWarningText>
              You have checked two or more locations with the same ID. Location IDs must be unique for {hostname}
            </LocationWarningText>
          )}
          {uneditableLocations.length > 0 && (
            <>
              <LocationWarningText>
                You have unchecked the following locations that currently only appear on {hostname}. Please add them to
                another hostname if you wish to uncheck them. You can copy the list below and paste it into the
                &quot;Check Locations&quot; box on another hostname.
              </LocationWarningText>
              <p>{uneditableLocations.join(",")}</p>
            </>
          )}
          <ChecklistScroll>
            <LocationCheckboxGrid>
              {visibleLocations.map((location) => (
                <LocationCheckboxRow key={location.locationId}>
                  <Checkbox
                    id={`location-checkbox-${location.locationId}`}
                    checked={checkedLocationIds.has(location.locationId)}
                    onChange={(e) => setLocationChecked(location.locationId, e.target.checked)}
                  />
                  <label htmlFor={`location-checkbox-${location.locationId}`}>{location.locationShortId}</label>
                </LocationCheckboxRow>
              ))}
            </LocationCheckboxGrid>
          </ChecklistScroll>
          {!hideSaveControls && (
            <LocationAssignmentControls>
              <BbotButton danger onClick={() => setVisibleLocationsChecked(false)}>
                Deselect All Visible Locations
              </BbotButton>
              <BbotButton
                type="primary"
                onClick={handleSave}
                disabled={duplicateLocationsError || uneditableLocations.length > 0 || isSaving}
              >
                Save
              </BbotButton>
            </LocationAssignmentControls>
          )}
        </div>
      )}
    </SettingsCard>
  );
};

export default LocationAssignmentSection;
